import { Component, Vue } from 'vue-property-decorator'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, GridOptions, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import i18n from '@/lang/i18n'
import GridHeader from '@/components/GridHeader.vue'
import {
    Column,
    IndexMonitoringModuleState,
    IndexMonitoringModuleSummary,
    initialIndexMonitoringModuleState
} from '@/modules/index-monitoring-module/index-monitoring-module.model'
import {
    INDEX_MONITORING_ENTITIES,
    statisticAnalysisAdminModuleService
} from '@/modules/index-monitoring-module/services/statistic-analysis-admin-module.service'
import {
    scientificAnalysisAdminModuleService
} from '@/modules/index-monitoring-module/services/scientific-analysis-admin-module.service'
import { StrictNullable } from '@/shared/types/app.types'
import { sumBy } from 'lodash'
import { CountItemsResponse } from '@/modules/index-monitoring-module/services/scientific-analysis-admin-module.dto'
import { AgGridRowModelType } from '@/shared/models/ag-grid.model'

@Component({
    components: {
        VtGrid,
        GridHeader
    }
})

export default class IndexMonitoringModule extends Vue {
    private selectedRows !: Array<object>
    private rowData!: Array<object>
    private columnDefs!: Array<object>
    private gridApi !: any

    state: IndexMonitoringModuleState
    constructor() {
        super()
        this.selectedRows = []
        this.columnDefs = [
            { type: 'customCheckbox' },
            {
                type: 'text',
                headerName: i18n.tc('Identificador'),
                colId: 'entityId',
                field: 'entityId',
                minWidth: 350,
                flex: 1,
                suppressSizeToFit: true,
                filter: true,
                sortable: false
            }
        ]
        this.rowData = [
            {
                entityId: '24234234'
            },
            {
                entityId: '45344454'
            },
            {
                entityId: '75654654'
            },
            {
                entityId: '22464568'
            },
            {
                entityId: '75347578'
            },
            {
                entityId: '90877764'
            }
        ]
        this.gridApi = null
        this.state = initialIndexMonitoringModuleState()
    }

    async getStatisticAnalysisAdminCount(): Promise<StrictNullable<CountItemsResponse[]>> {
        return this.$_toCallUnique(statisticAnalysisAdminModuleService.getStatisticAnalysisAdminCount())
    }

    async getScientificAnalysisAdminCount(): Promise<StrictNullable<CountItemsResponse[]>> {
        return this.$_toCallUnique(scientificAnalysisAdminModuleService.getScientificAnalysisAdminCount())
    }

    getItemsQueue(itemsQueue: StrictNullable<number>): number {
        return itemsQueue ?? 0
    }

    getTranslatedEntityName(entityCode: string): string {
        return INDEX_MONITORING_ENTITIES[entityCode]?.name
    }

    async created(): Promise<void> {
        const statisticAnalysisAdminCount = await this.getStatisticAnalysisAdminCount()
        if (statisticAnalysisAdminCount) {
            this.state.statisticAnalysis = statisticAnalysisAdminCount
        }
        const scientificAnalysisAdminCount = await this.getScientificAnalysisAdminCount()
        if (scientificAnalysisAdminCount) {
            this.state.scientificAnalysis = scientificAnalysisAdminCount
        }
    }

    get gridOptions(): GridOptions {
        return {
            rowModelType: AgGridRowModelType.CLIENT_SIDE,
            getContextMenuItems: this.getContextMenuItems,
            onGridReady: params => {
                this.onGridReady(params)
            },
            onRowSelected: params => {
                this.onRowSelected()
            }
        }
    }

    get scientificAnalysisSummary(): IndexMonitoringModuleSummary[] {
        const headerColumns = this.state.columns.filter(column => column.id !== Column.ENTITY)
        return headerColumns.map(headerColumn => {
            return {
                columnTranslatedName: headerColumn.translatedName,
                summary: this.getSummary(headerColumn.id, this.state.scientificAnalysis)
            }
        })
    }

    get statisticAnalysisSummary(): IndexMonitoringModuleSummary[] {
        const headerColumns = this.state.columns.filter(column => column.id !== Column.ENTITY)
        return headerColumns.map(headerColumn => {
            return {
                columnTranslatedName: headerColumn.translatedName,
                summary: this.getSummary(headerColumn.id, this.state.statisticAnalysis)
            }
        })
    }

    getSummary(columnId: Column, countItems: CountItemsResponse[]): number {
        let summary = 0
        if (columnId === Column.ELASTIC_SEARCH) {
            summary = sumBy(countItems, Column.ELASTIC_SEARCH)
        } else if (columnId === Column.DATABASE) {
            summary = sumBy(countItems, Column.DATABASE)
        } else if (columnId === Column.QUEUED) {
            summary = sumBy(countItems, Column.QUEUED)
        }
        return summary
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        if (!params?.node) {
            return []
        } else {
            return [
                {
                    name: this.$t('configuration.actions.update').toString()
                },
                {
                    name: this.$t('configuration.actions.delete').toString()
                },
                {
                    name: this.$t('configuration.actions.activate').toString()
                },
                {
                    name: this.$t('configuration.actions.deactivate').toString()
                }
            ]
        }
    }

    onRowSelected() {
        this.selectedRows = this.gridApi.getSelectedRows()
    }

    onGridReady({ api }: any) {
        this.gridApi = api
    }
}
